import React, { useEffect, useState, useCallback } from "react";
import { getLocationsDetails, getSgAddressByPostalCode } from "../service/location";
import { countryList, defaultCountrySettings, googleApiKey, inGeoCode, ipStackKey, sgGeoCode, usGeoCode } from "../utils/constants";
import { useRouter } from "next/router";

export const LocationContext = React.createContext();

export const LocationProvider = ({ children, }) => {
    const router = useRouter();

    const [ googleMaps, setGoogleMaps ] = useState("")
    const [ countryName, setCountryName ]  = useState(defaultCountrySettings.countryName);
    const [ countryCd, setCountryCd ] = useState(defaultCountrySettings.countryCd);
    const [ countryCurrCd, setCountryCurrCd ] = useState(defaultCountrySettings.countryCurrCd);
    const [ countryTelCd, setCountryTelCd ] = useState(defaultCountrySettings.countryTelCd);
    const [ userIpDetails, setUserIpDetails ] = useState("");
    const [ location, setLocation ] = useState("Singapore, Singapore");
    const [ userLatitude, setUserLatitude ] = useState(1.3521);
    const [ userLongitude, setUserLongitude ] = useState(103.8198);
    const [ city, setCity ] = useState("Singapore");
    const [ locality, setLocality ] = useState("Singapore")
   
    useEffect(() => {
        if(!router.query.countryCd) return
        setCountryCd(router.query.countryCd)
    }, [router.query.countryCd, setCountryCd])

    useEffect(() => {
        if(!router.query.locality) return
        setLocality(router.query.locality)
    }, [router.query.locality, setLocality])

    const getCountryList = function(countryNm){
        for(let country in countryList){
          if(countryNm === countryList[country].country_name){
            setCountryCd(countryList[country].country_cd);
            setCountryCurrCd(countryList[country].country_curr_cd);
            setCountryTelCd(countryList[country].country_tel_cd);
          }
        }
    }
    
    const setCityAsLocation = async (city, country) => {
        setLocation(city +", " + country)
    }
    
    const setLocationDetailsFromIp = useCallback((response) => {
        setUserIpDetails(response)
        setCountryName(response.country_name)
        setCityAsLocation(response.city, response.country_name)
        setUserLatitude(response.latitude)
        setUserLongitude(response.longitude)
        setCity(response.city)
        if(!locality){
            setLocality(response.city)
        }
        getCountryList(response.country_name)
    }, [])

    const setCountryFromIp = useCallback(() =>{
        fetch('https://api.ipstack.com/check?access_key='+ipStackKey)
            .then(res => res.json())
            .then((response) => {
                setLocationDetailsFromIp(response)
            }). catch(error => {
                console.log(error)
            });
    }, [setLocationDetailsFromIp])
    
    const getCountryFromRoute = useCallback(() => {
        if(!router) return

        let countrySet = false
        if(router.pathname === "/" || router.pathname.startsWith("/sg") || router.pathname.startsWith("/services/sg")){
            setLocationDetailsFromIp(sgGeoCode)  
            countrySet = true
        }
        if(router.pathname.startsWith("/us") || router.pathname.startsWith("/services/us")){
            setLocationDetailsFromIp(usGeoCode)
            countrySet = true
        }
        if(router.pathname.startsWith("/in") || router.pathname.startsWith("/services/in")){
            setLocationDetailsFromIp(inGeoCode)
            countrySet = true
        }
        return countrySet
    },  [setLocationDetailsFromIp, router])

    useEffect(() => {    
        if(!router) return  

        getCountryFromRoute()

    }, [getCountryFromRoute, router])

    const setCountry = async (locationDetails) => {
        for(let i = 0; i < locationDetails.results[0].address_components.length; i++){
             for(let x = 0; x < locationDetails.results[0].address_components[i].types.length; x++){
                 if(locationDetails.results[0].address_components[i].types[x] == "country"){
                   setCountryName(locationDetails.results[0].address_components[i].long_name);      			  
                   getCountryList(locationDetails.results[0].address_components[i].long_name);
                 }
             }
         }
    }

    const getLocality = (address_components) => {
        for(let x in address_components){
            for(let i in address_components[x].types){
                if(address_components[x].types[i] == "neighborhood"){
                    return address_components[x].long_name
                }
                if(address_components[x].types[i] == "sublocality"){
                    return address_components[x].long_name
                }
                if(address_components[x].types[i] == "administrative_area_level_2"){
                    return address_components[x].long_name
                }
                if(address_components[x].types[i] == "administrative_area_level_1"){
                    return address_components[x].long_name
                }
            }
        }
    }

    const getUserLocationDetails = useCallback(async () => {
        //setCountryFromIp()
        await getLocationsDetails()
            .then( async(result) => {
                setLocation(result.locationDetails.results[0].formatted_address);
                setUserLatitude(result.userLocation.latitude);
                setUserLongitude(result.userLocation.longitude);
                setCountry(result.locationDetails);
                const locale = getLocality(result.locationDetails.results[0].address_components)
                setLocality(locale)                
            }, (errorCode) => {
                if(errorCode === false) {
                    alert('GeoLocation is not supported by browser.');
                }
                else if(errorCode == 1) {                    
                    //setIsGetIpGeocode(true)
                    if(!getCountryFromRoute()){
                        setCountryFromIp();
                    }
                }
            }
        );      
    }, [])

    // Set location details for Singapore from a postal code using OneMap API
    const setSgLocationByPostalCode = useCallback(async (postalCode) => {
        const result = await getSgAddressByPostalCode(postalCode);
        // Set Singapore country details
        setCountryName("Singapore");
        getCountryList("Singapore");
        // Set location info
        setLocation(result.fullAddress || result.address);
        setUserLatitude(result.latitude);
        setUserLongitude(result.longitude);
        setLocality(result.roadName || result.building || "Singapore");
        setCity("Singapore");
        return result;
    }, [])

    // useEffect(() => {
    //     getUserLocationDetails()
    // }, [getUserLocationDetails])

    // useEffect(() => {
    //     if(!isGetIpGeocode || countryName) return
    //     console.log("use location from Ipstack")
    //     setCountryFromIp();
    // }, [isGetIpGeocode])

    return (
    <LocationContext.Provider
        value={{
            setLocationDetailsFromIp,
            getUserLocationDetails,
            setSgLocationByPostalCode,
            countryName,
            city,
            locality,
            countryCd,
            countryCurrCd,
            countryTelCd,
            location,
            userLatitude,
            setUserLatitude,
            userLongitude,
            setUserLongitude,
            googleMaps,
            setGoogleMaps
        }}
    >
        {children}
        {/* <Script
            id="google-maps"
            src={`https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`}
            strategy="lazyOnload"
            onLoad={() => {
                //getUserLocationDetails()
            }}
        /> */}
    </LocationContext.Provider>
    )
}
